import React, { useMemo } from 'react';
import Button from 'components/Button';
import {
  LISTING_CONTACT_METHOD_ORDER,
  ListingContactMethod,
  type ListingContactMethodValue,
} from 'constants/listingContactMethod';
import { buildWhatsappClickToChatUrl } from 'utils/whatsappClickToChat';
import { formatPhoneForDisplay, normalizePhoneToInternationalDigits } from 'utils/phoneNumber';
import './ListingContactActions.css';

export interface ListingContactActionsProps {
  listingId: string;
  listingCode: string;
  contactMethods: ListingContactMethodValue[];
  sellerPhoneNumber?: string | null;
  sellerWhatsappPhoneDigits?: string | null;
  isListingOwner: boolean;
  onDirectMessage: () => void;
}

const TOP_ROW_ORDER: ListingContactMethodValue[] = [
  ListingContactMethod.DirectMessage,
  ListingContactMethod.Whatsapp,
];

function resolveActiveMethods(
  contactMethods: ListingContactMethodValue[],
  sellerPhoneNumber?: string | null,
  sellerWhatsappPhoneDigits?: string | null
): ListingContactMethodValue[] {
  return LISTING_CONTACT_METHOD_ORDER.filter((method) => {
    if (!contactMethods.includes(method)) return false;
    if (method === ListingContactMethod.DirectMessage) return true;
    if (method === ListingContactMethod.Phone) {
      return Boolean(
        normalizePhoneToInternationalDigits(sellerPhoneNumber ?? '') ||
          sellerWhatsappPhoneDigits?.trim()
      );
    }
    if (method === ListingContactMethod.Whatsapp) {
      return Boolean(
        sellerWhatsappPhoneDigits?.trim() ||
          normalizePhoneToInternationalDigits(sellerPhoneNumber ?? '')
      );
    }
    return false;
  });
}

const ListingContactActions: React.FC<ListingContactActionsProps> = ({
  listingCode,
  contactMethods,
  sellerPhoneNumber,
  sellerWhatsappPhoneDigits,
  isListingOwner,
  onDirectMessage,
}) => {
  const active = useMemo(
    () => resolveActiveMethods(contactMethods, sellerPhoneNumber, sellerWhatsappPhoneDigits),
    [contactMethods, sellerPhoneNumber, sellerWhatsappPhoneDigits]
  );

  if (isListingOwner || active.length === 0) return null;

  const phoneDigits =
    sellerWhatsappPhoneDigits ??
    normalizePhoneToInternationalDigits(sellerPhoneNumber ?? '') ??
    '';
  const phoneDisplay =
    sellerPhoneNumber?.trim() ||
    formatPhoneForDisplay(sellerPhoneNumber) ||
    (phoneDigits ? `+${phoneDigits}` : '');

  const topMethods = TOP_ROW_ORDER.filter((m) => active.includes(m));
  const showPhone = active.includes(ListingContactMethod.Phone) && Boolean(phoneDigits);

  const renderCompactAction = (method: ListingContactMethodValue) => {
    if (method === ListingContactMethod.DirectMessage) {
      return (
        <div className="listing-contact-actions__slot listing-contact-actions__slot--compact">
          <Button
            type="button"
            className="btn btn-primary listing-contact-actions__action"
            onClick={onDirectMessage}
            aria-label="Direkt Mesaj"
          >
            <i className="fas fa-comment listing-contact-actions__icon" aria-hidden="true" />
            <span className="listing-contact-actions__label">Direkt Mesaj</span>
          </Button>
        </div>
      );
    }

    if (method === ListingContactMethod.Whatsapp && phoneDigits) {
      const waUrl = buildWhatsappClickToChatUrl(phoneDigits, listingCode);
      return (
        <div className="listing-contact-actions__slot listing-contact-actions__slot--compact">
          <a
            href={waUrl}
            className="btn btn-success listing-contact-actions__action"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="WhatsApp"
          >
            <i className="fab fa-whatsapp listing-contact-actions__icon" aria-hidden="true" />
            <span className="listing-contact-actions__label">WhatsApp</span>
          </a>
        </div>
      );
    }

    return null;
  };

  const renderPhoneAction = () => {
    if (!showPhone) return null;
    const telHref = `tel:+${phoneDigits}`;
    return (
      <div className="listing-contact-actions__slot listing-contact-actions__slot--phone">
        <a
          href={telHref}
          className="btn btn-outline-primary listing-contact-actions__action"
          aria-label={`Telefon: ${phoneDisplay}`}
        >
          <i className="fas fa-phone listing-contact-actions__icon" aria-hidden="true" />
          <span className="listing-contact-actions__label">{phoneDisplay}</span>
        </a>
      </div>
    );
  };

  const renderLegacySingle = (method: ListingContactMethodValue) => {
    if (method === ListingContactMethod.DirectMessage) {
      return renderCompactAction(method);
    }
    if (method === ListingContactMethod.Whatsapp) {
      return renderCompactAction(method);
    }
    if (method === ListingContactMethod.Phone) {
      return renderPhoneAction();
    }
    return null;
  };

  const useSplitLayout = topMethods.length > 0 && showPhone;

  if (useSplitLayout) {
    return (
      <div className="listing-contact-actions">
        <div className="listing-contact-actions__top row g-2">
          {topMethods.map((method) => (
            <div
              key={method}
              className={topMethods.length === 1 ? 'col-12' : 'col-6'}
            >
              {renderCompactAction(method)}
            </div>
          ))}
        </div>
        {renderPhoneAction()}
      </div>
    );
  }

  if (topMethods.length > 0 && !showPhone) {
    return (
      <div className="listing-contact-actions">
        <div className="listing-contact-actions__top row g-2">
          {topMethods.map((method) => (
            <div
              key={method}
              className={topMethods.length === 1 ? 'col-12' : 'col-6'}
            >
              {renderCompactAction(method)}
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="listing-contact-actions">
      {active.map((method) => (
        <React.Fragment key={method}>{renderLegacySingle(method)}</React.Fragment>
      ))}
    </div>
  );
};

export default ListingContactActions;
